import { Injectable, signal, computed } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { HttpClientService } from './http-client.service';

export interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  createdAt: string;
}

export interface AuthResponse {
  token: string;
  user: User;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

export interface UpdateProfileRequest {
  firstName?: string;
  lastName?: string;
  email?: string;
}

export interface TwoFactorRequired {
  twoFactorRequired: true;
  challenge: string;
}

export type LoginResult = AuthResponse | TwoFactorRequired;

export interface TwoFactorSetup {
  secret: string;
  otpauthUri: string;
}

export function isTwoFactorRequired(result: LoginResult): result is TwoFactorRequired {
  return 'twoFactorRequired' in result;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUserSignal = signal<User | null>(null);
  private tokenKey = 'auth_token';

  // Public readonly signal
  currentUser = this.currentUserSignal.asReadonly();

  // Computed signal voor isLoggedIn
  isLoggedIn = computed(() => this.currentUserSignal() !== null);

  constructor(private http: HttpClientService) {
    this.loadStoredUser();
  }

  private loadStoredUser(): void {
    const token = localStorage.getItem(this.tokenKey);
    if (token) {
      try {
        // Decode JWT to extract user info (without verification - backend will verify)
        const payload = JSON.parse(atob(token.split('.')[1]));

        // Check if token is expired (basic client-side check)
        if (payload.exp && payload.exp > Date.now() / 1000) {
          // Set user from token payload
          // The token will be validated on the next API call
          // If invalid, the backend will return 401 and we'll logout
          this.currentUserSignal.set({
            id: payload.id,
            email: payload.username,
            firstName: payload.firstName || '',
            lastName: payload.lastName || '',
            createdAt: payload.createdAt || ''
          });
        } else {
          // Token expired, clear it
          this.logout();
        }
      } catch (error) {
        // Invalid token format, clear it
        this.logout();
      }
    }
  }

  login(credentials: LoginRequest): Observable<LoginResult> {
    return this.http.post<LoginResult>('/auth/login', credentials).pipe(
      tap(response => {
        if ('token' in response) {
          localStorage.setItem(this.tokenKey, response.token);
          this.currentUserSignal.set(response.user);
        }
      })
    );
  }

  verifyTwoFactor(challenge: string, code: string): Observable<AuthResponse> {
    return this.http.post<AuthResponse>('/auth/2fa/verify', { challenge, code }).pipe(
      tap(response => {
        localStorage.setItem(this.tokenKey, response.token);
        this.currentUserSignal.set(response.user);
      })
    );
  }

  twoFactorStatus(): Observable<{ enabled: boolean }> {
    return this.http.get<{ enabled: boolean }>('/profile/2fa/status');
  }

  setup2fa(): Observable<TwoFactorSetup> {
    return this.http.post<TwoFactorSetup>('/profile/2fa/setup', {});
  }

  enable2fa(code: string): Observable<{ backupCodes: string[] }> {
    return this.http.post<{ backupCodes: string[] }>('/profile/2fa/enable', { code });
  }

  disable2fa(code: string): Observable<{ enabled: boolean }> {
    return this.http.post<{ enabled: boolean }>('/profile/2fa/disable', { code });
  }

  register(data: RegisterRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>('/auth/register', data).pipe(
      tap(response => {
        localStorage.setItem(this.tokenKey, response.token);
        this.currentUserSignal.set(response.user);
      })
    );
  }

  updateProfile(data: UpdateProfileRequest): Observable<AuthResponse> {
    // The API returns a fresh JWT (its payload carries name/e-mail), so we
    // replace the stored token and refresh the current-user signal.
    return this.http.patch<AuthResponse>('/profile', data).pipe(
      tap(response => {
        localStorage.setItem(this.tokenKey, response.token);
        this.currentUserSignal.set(response.user);
      })
    );
  }

  forgotPassword(email: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>('/auth/forgot-password', { email });
  }

  resetPassword(token: string, password: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>('/auth/reset-password', { token, password });
  }

  logout(): void {
    localStorage.removeItem(this.tokenKey);
    this.currentUserSignal.set(null);
  }

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }
}
